import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "okkio_cookie_consent";
const CONSENT_VERSION = "2026-08-18";
const SIX_MONTHS_MS = 1000 * 60 * 60 * 24 * 182;
export const OPEN_PREFS_EVENT = "okkio:open-cookie-preferences";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (c.version !== CONSENT_VERSION) return null;
    if (!c.ts || Date.now() - c.ts > SIX_MONTHS_MS) return null;
    return c;
  } catch {
    return null;
  }
}

function writeConsent(analytics, marketing) {
  const c = { version: CONSENT_VERSION, analytics, marketing, ts: Date.now() };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(c)); } catch { /* ignore */ }
  return c;
}

import { applyMetaConsent } from "@/lib/metaPixel";

function applyPosthog(analyticsAllowed) {
  try {
    const p = window.posthog;
    if (!p) return;
    if (analyticsAllowed) {
      if (!window.__okkioPhInited && p.init && window.__OKKIO_PH_KEY) {
        p.init(window.__OKKIO_PH_KEY, window.__OKKIO_PH_OPTS || {});
        window.__okkioPhInited = true;
      }
      if (p.opt_in_capturing) p.opt_in_capturing();
      if (p.startSessionRecording) p.startSessionRecording();
    } else if (window.__okkioPhInited) {
      // Inizializzato in precedenza (consenso poi revocato): ferma tutto.
      if (p.stopSessionRecording) p.stopSessionRecording();
      if (p.opt_out_capturing) p.opt_out_capturing();
    }
    // Se non ancora inizializzato e senza consenso: nessuna azione, nessun caricamento SDK/rete.
  } catch { /* ignore */ }
}

const Toggle = ({ checked, onChange, disabled, testid }) => (
  <button type="button" role="switch" aria-checked={checked} disabled={disabled}
    data-testid={testid}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${disabled ? "bg-[#CBD5E1] cursor-not-allowed" : checked ? "bg-[#3157F6]" : "bg-[#CBD5E1]"}`}>
    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`} />
  </button>
);

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const panelRef = useRef(null);
  const customizeRef = useRef(null);
  const wasPanelOpen = useRef(false);

  useEffect(() => {
    if (!showPanel) return;
    const prevFocus = document.activeElement;
    const getFocusable = () => {
      const el = panelRef.current;
      if (!el) return [];
      return Array.from(el.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])')).filter((n) => !n.disabled);
    };
    const f = getFocusable();
    if (f[0]) f[0].focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); setShowPanel(false); return; }
      if (e.key === "Tab") {
        const nodes = getFocusable();
        if (!nodes.length) return;
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (prevFocus && prevFocus.focus && document.contains(prevFocus)) prevFocus.focus();
    };
  }, [showPanel]);

  // Ripristina il focus sul pulsante "Personalizza" del banner quando il pannello si chiude
  useEffect(() => {
    if (wasPanelOpen.current && !showPanel && showBanner) {
      requestAnimationFrame(() => customizeRef.current && customizeRef.current.focus());
    }
    wasPanelOpen.current = showPanel;
  }, [showPanel, showBanner]);

  useEffect(() => {
    const c = readConsent();
    if (!c) { setShowBanner(true); applyPosthog(false); applyMetaConsent(false); }
    else { setAnalytics(!!c.analytics); setMarketing(!!c.marketing); applyPosthog(!!c.analytics); applyMetaConsent(!!c.marketing); }
  }, []);

  const openPrefs = useCallback(() => {
    const c = readConsent();
    setAnalytics(c ? !!c.analytics : false);
    setMarketing(c ? !!c.marketing : false);
    setShowPanel(true);
  }, []);

  useEffect(() => {
    window.addEventListener(OPEN_PREFS_EVENT, openPrefs);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, openPrefs);
  }, [openPrefs]);

  const acceptAll = () => { writeConsent(true, true); applyPosthog(true); applyMetaConsent(true); setShowBanner(false); setShowPanel(false); };
  const rejectAll = () => { writeConsent(false, false); applyPosthog(false); applyMetaConsent(false); setShowBanner(false); setShowPanel(false); };
  const savePrefs = () => { writeConsent(analytics, marketing); applyPosthog(analytics); applyMetaConsent(marketing); setShowBanner(false); setShowPanel(false); };

  if (!showBanner && !showPanel) return null;

  return (
    <>
      {/* Banner primo accesso */}
      {showBanner && !showPanel && (
        <div data-testid="cookie-banner"
          className="fixed inset-x-0 bottom-0 z-[60] p-2.5 sm:p-5 pb-[max(10px,env(safe-area-inset-bottom))]">
          <div className="max-w-3xl mx-auto rounded-2xl border border-[#E2E8F0] bg-white shadow-[0_20px_60px_-20px_rgba(15,23,42,0.35)] px-4 py-2.5 sm:p-6 relative">
            <button onClick={rejectAll} data-testid="cookie-close" aria-label="Chiudi e rifiuta i cookie non necessari"
              className="absolute top-2 right-2 sm:top-3 sm:right-3 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-[#64748B] hover:bg-[#F1F5F9] transition-colors">
              <X className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2} />
            </button>
            <div className="flex items-start gap-2.5 sm:gap-3 pr-8">
              <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-[#3157F6]/10 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-4 h-4 sm:w-5 sm:h-5 text-[#3157F6]" strokeWidth={1.9} />
              </span>
              <div>
                <h2 className="font-heading text-sm sm:text-lg font-semibold text-[#0F172A] leading-tight">Rispettiamo la tua privacy</h2>
                <p className="mt-0.5 sm:mt-1 text-[12px] sm:text-sm text-[#475569] leading-[1.25] sm:leading-relaxed">
                  Usiamo solo cookie tecnici necessari al funzionamento del sito. Non attiviamo cookie analitici o di marketing senza il tuo consenso. Puoi accettare, rifiutare o scegliere in dettaglio. Chiudendo (X) mantieni solo i cookie necessari.{" "}
                  <Link to="/cookie-policy" className="font-semibold text-[#3157F6] hover:underline">Cookie Policy</Link>
                </p>
              </div>
            </div>
            <div className="mt-2 sm:mt-5 grid grid-cols-2 sm:flex sm:flex-row gap-1.5 sm:gap-2.5">
              <button onClick={acceptAll} data-testid="cookie-accept-all"
                className="col-span-2 sm:flex-1 rounded-xl h-11 sm:h-12 px-5 text-sm font-semibold bg-[#3157F6] hover:bg-[#2444D8] text-white transition-colors">
                Accetta tutti
              </button>
              <button onClick={rejectAll} data-testid="cookie-reject-all"
                className="sm:flex-1 rounded-xl h-11 sm:h-12 px-4 text-sm font-semibold border border-[#CBD5E1] text-[#0F172A] hover:bg-[#F1F5F9] transition-colors">
                Rifiuta
              </button>
              <button ref={customizeRef} onClick={() => setShowPanel(true)} data-testid="cookie-customize"
                className="sm:flex-1 rounded-xl h-11 sm:h-12 px-4 text-sm font-semibold text-[#3157F6] hover:bg-[#EEF2FF] transition-colors">
                Personalizza
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pannello preferenze granulare */}
      {showPanel && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-[#0F172A]/50" onClick={() => { if (!showBanner) setShowPanel(false); }} />
          <div ref={panelRef} role="dialog" aria-modal="true" aria-label="Preferenze cookie" data-testid="cookie-panel"
            className="relative w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Preferenze cookie</h2>
              <button onClick={() => setShowPanel(false)} data-testid="cookie-panel-close" aria-label="Chiudi"
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#64748B] hover:bg-[#F1F5F9]">
                <X className="w-5 h-5" strokeWidth={2} />
              </button>
            </div>
            <p className="mt-2 text-sm text-[#475569] leading-relaxed">
              Gestisci le categorie di cookie. I cookie necessari sono sempre attivi. Le altre categorie restano disattivate finché non le abiliti. Dettagli nella{" "}
              <Link to="/cookie-policy" onClick={() => setShowPanel(false)} className="font-semibold text-[#3157F6] hover:underline">Cookie Policy</Link>.
            </p>

            <div className="mt-5 space-y-3">
              <div className="rounded-xl border border-[#E2E8F0] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-heading font-semibold text-[#0F172A]">Necessari</p>
                    <p className="text-xs text-[#64748B]">Indispensabili al funzionamento (sessione, preferenze cookie, pagamento). Sempre attivi.</p>
                  </div>
                  <Toggle checked disabled testid="cookie-necessary-toggle" onChange={() => {}} />
                </div>
              </div>

              <div className="rounded-xl border border-[#E2E8F0] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-heading font-semibold text-[#0F172A]">Analitici</p>
                    <p className="text-xs text-[#64748B]">Statistiche aggregate d’uso. Attualmente non utilizziamo cookie analitici di terze parti.</p>
                  </div>
                  <Toggle checked={analytics} onChange={setAnalytics} testid="cookie-analytics-toggle" />
                </div>
              </div>

              <div className="rounded-xl border border-[#E2E8F0] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-heading font-semibold text-[#0F172A]">Marketing / Profilazione</p>
                    <p className="text-xs text-[#64748B]">Cookie pubblicitari o di profilazione. Attualmente non ne utilizziamo.</p>
                  </div>
                  <Toggle checked={marketing} onChange={setMarketing} testid="cookie-marketing-toggle" />
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              <button onClick={savePrefs} data-testid="cookie-save"
                className="flex-1 rounded-xl h-12 px-5 text-sm font-semibold bg-[#3157F6] hover:bg-[#2444D8] text-white transition-colors">
                Salva preferenze
              </button>
              <button onClick={rejectAll} data-testid="cookie-panel-reject"
                className="rounded-xl h-12 px-5 text-sm font-semibold border border-[#CBD5E1] text-[#0F172A] hover:bg-[#F1F5F9] transition-colors">
                Rifiuta tutti
              </button>
              <button onClick={acceptAll} data-testid="cookie-panel-accept"
                className="rounded-xl h-12 px-5 text-sm font-semibold border border-[#CBD5E1] text-[#0F172A] hover:bg-[#F1F5F9] transition-colors">
                Accetta tutti
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
